import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { cn } from './cn';

export type ToastTone = 'info' | 'success' | 'warning' | 'critical';

interface ToastEntry {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  duration: number;
}

interface ToastAPI {
  show: (t: { tone?: ToastTone; title: string; description?: string; duration?: number }) => void;
  dismiss: (id: string) => void;
}

const ToastCtx = createContext<ToastAPI | null>(null);

export const useToast = (): ToastAPI => {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be inside <ToastProvider>');
  return ctx;
};

const TONE_CLASSES: Record<ToastTone, { ring: string; bar: string; title: string }> = {
  info:     { ring: 'ring-brand/15',    bar: 'bg-brand',        title: 'text-ink' },
  success:  { ring: 'ring-sev-maintenance/20', bar: 'bg-sev-maintenance', title: 'text-ink' },
  warning:  { ring: 'ring-sev-major/20', bar: 'bg-sev-major',   title: 'text-ink' },
  critical: { ring: 'ring-sev-critical/20', bar: 'bg-sev-critical', title: 'text-ink' },
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timers = useRef<Map<string, number>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) { window.clearTimeout(timer); timers.current.delete(id); }
  }, []);

  const show: ToastAPI['show'] = useCallback(({ tone = 'info', title, description, duration = 4000 }) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const entry: ToastEntry = { id, tone, title, description, duration };
    setToasts(prev => [...prev, entry]);
    if (duration > 0) {
      const timer = window.setTimeout(() => dismiss(id), duration);
      timers.current.set(id, timer);
    }
  }, [dismiss]);

  useEffect(() => () => {
    timers.current.forEach(t => window.clearTimeout(t));
    timers.current.clear();
  }, []);

  return (
    <ToastCtx.Provider value={{ show, dismiss }}>
      {children}
      <div
        className="fixed top-4 right-4 z-[70] flex flex-col gap-2 max-w-sm pointer-events-none"
        role="region"
        aria-label="Notifications"
      >
        {toasts.map(t => {
          const tone = TONE_CLASSES[t.tone];
          return (
            <div
              key={t.id}
              className={cn(
                'pointer-events-auto relative overflow-hidden bg-surface-1 rounded-xl',
                'shadow-popover ring-1 ring-inset', tone.ring,
                'animate-fade-up',
              )}
              role="status"
            >
              <span className={cn('absolute inset-y-0 left-0 w-1', tone.bar)} aria-hidden />
              <div className="pl-4 pr-3 py-3.5 flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className={cn('text-body-sm font-semibold', tone.title)}>{t.title}</p>
                  {t.description && (
                    <p className="text-caption text-ink-subtle mt-0.5">{t.description}</p>
                  )}
                </div>
                <button
                  onClick={() => dismiss(t.id)}
                  aria-label="Dismiss"
                  className="-mr-1 p-1.5 text-ink-faint hover:text-ink rounded-md"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastCtx.Provider>
  );
};
